import {
  Button,
  mediaQueries,
  ButtonLink,
  palette,
} from '@centivo/react-components'
import styled from 'styled-components'

import { ContentSection } from 'src/refactor/structure'

import ImageHero from 'src/assets/Images/backgrounds/hero-image.svg'
import VideoImageHero from 'src/assets/Images/backgrounds/hero-image-video.svg'
import PlayIcon from 'src/assets/Images/icons/play-icon.svg'
import { useGlobalState } from 'src/context/global'
import { isEmpty } from 'lodash'
import React, { useMemo } from 'react'

const HeroSectionTag = styled(ContentSection)`
  display: grid;
  grid-template-columns: 100%;
  justify-content: space-between;
  margin-bottom: 1.5rem;

  @media ${mediaQueries.mobile} {
    max-width: 380px;
  }

  @media ${mediaQueries.desktop} {
    grid-template-columns: minmax(0, 670px) auto;
  }

  .left {
    margin-bottom: 2.5rem;

    @media ${mediaQueries.desktop} {
      margin-bottom: 0;
    }
    h2 {
      font-size: 2.5rem;
      font-weight: 700;
      line-height: 1.3;
      padding: 3rem 0 2rem;
      color: ${palette.brand.darkGreen};

      @media ${mediaQueries.desktop} {
        font-size: 3.5rem;
        line-height: 1.14;
      }
    }
    .hero-button {
      padding: 1rem 2.5rem 0.9rem;
      font-size: 0.875rem;
      background-color: ${palette.brand.orange};
      color: ${palette.brand.brown};
      border-radius: 10px;

      &:first-of-type {
        margin-bottom: 1rem;
        @media (min-width: 767px) {
          margin-right: 1.25rem;
          margin-bottom: 0;
        }
      }
    }
    a.hero-button:hover {
      color: ${palette.brand.orange};
    }
  }

  .right {
    text-align: center;
    position: relative;
    background-color: transparent;
    border: none;
    padding: none;
    margin: none;

    &:not(.is-video) {
      cursor: default;
      pointer-events: none;
    }
    &.is-video {
      > * {
        cursor: pointer;
      }

      &:hover {
        div.play-button {
          filter: drop-shadow(0px 2px 4px rgba(241, 93, 42, 0.7));
          transition-duration: 80ms;
        }
      }
    }

    @media ${mediaQueries.desktop} {
      text-align: unset;
      display: flex;
      align-items: center;
    }

    > img {
      width: 100%;
      object-fit: cover;
    }

    div.play-button {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      justify-content: center;
      align-items: center;
      height: 3.125rem;
      aspect-ratio: 1;
      border-radius: 50%;
      background-color: ${palette.brand.orange};

      img {
        padding-left: 0.25rem;
      }
    }
  }
`

export const HeroSectionDefault: React.FC<{
  setVideoPlayerOpen: (open: boolean) => void
}> = ({ setVideoPlayerOpen }) => {
  const [{ appConfig }] = useGlobalState()

  const {
    template,
    headerText,
    heroBtn,
    heroImage: personalizedHeroImage,
    video,
  } = appConfig

  const goToForm = () => {
    const element = document.getElementById('find-a-doctor')

    if (element) {
      const top = element.getClientRects()[0].top - 50

      window.scrollTo({
        top,
        behavior: 'smooth',
      })
    }
  }

  const heroImage = useMemo(() => {
    if (!isEmpty(personalizedHeroImage)) return personalizedHeroImage
    if (video.enabled) return VideoImageHero
    return ImageHero
  }, [personalizedHeroImage, video])

  const handleVideoPlay = () => {
    if (!video.enabled) return
    setVideoPlayerOpen(true)
  }

  return (
    <HeroSectionTag>
      <div className="left">
        <h2>{headerText}</h2>
        {heroBtn?.enabled && (
          <ButtonLink
            className="hero-button"
            edgeType="default"
            buttonType="secondary"
            href={heroBtn.link}
          >
            {heroBtn.text}
          </ButtonLink>
        )}
        {template !== 'template-3' && (
          <Button className="hero-button" onClick={goToForm}>
            Find a doctor or facility
          </Button>
        )}
      </div>
      <button
        className={`right ${video.enabled ? 'is-video' : ''}`}
        type="button"
        onClick={handleVideoPlay}
      >
        <img
          src={heroImage}
          alt="A doctor checking the heartbeat of a child in his mother's arms"
        />
        {video.enabled && (
          <div className="play-button">
            <img src={PlayIcon} alt="Play icon" />
          </div>
        )}
      </button>
    </HeroSectionTag>
  )
}
